<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BaseModal from '../ui/BaseModal.vue';
import BaseButton from '../ui/BaseButton.vue';
import BaseAlert from '../ui/BaseAlert.vue';
import I18nHtml from '../ui/I18nHtml.vue';
import { useModalStore, ModalType } from '@/stores/modal.ts';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration.ts';
import DateAndDurationSelector from '@/components/shared/DateAndDurationSelector.vue';
import type { CalendarRestriction, DateRange } from '@/types/SearchBar.ts';
import { isPreset, nightsInRange, type DurationMode } from '@/utils/filters/durationMode.ts';
import { MAX_PRODUCT_DURATION } from '@/utils/filters/durationPlan.ts';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  dateRange: DateRange;
  duration: number;
  durationMode?: DurationMode;
  minDate?: string;
  maxDate?: string;
  restriction?: CalendarRestriction;
}

const props = withDefaults(defineProps<Props>(), {
  durationMode: 'preset',
  minDate: undefined,
  maxDate: undefined,
  restriction: undefined,
});

const emit = defineEmits<{
  'update:dateRange': [value: DateRange];
  'update:duration': [value: number];
  'update:durationMode': [value: DurationMode];
}>();

const modalStore = useModalStore();
const filterConfigurationStore = useFilterConfigurationStore();

const localDateRange = ref<DateRange>(null);
const localDuration = ref<number>(props.duration);
const localDurationMode = ref<DurationMode>(props.durationMode);
const adHocDuration = ref<number | null>(null);
const selectionPending = ref(false);

const isDatePickerModalOpen = computed({
  get: () => modalStore.isModalOpen(ModalType.DATE_PICKER),
  set: (value: boolean) => {
    if (!value) {
      modalStore.closeModal(ModalType.DATE_PICKER);
    }
  },
});

watch(
  () => modalStore.isModalOpen(ModalType.DATE_PICKER),
  (isOpen) => {
    if (isOpen) {
      selectionPending.value = false;
      localDateRange.value = props.dateRange;
      localDuration.value = props.duration;

      const isAdHoc =
        props.durationMode !== 'exact' &&
        props.duration > 0 &&
        !isPreset(props.duration, filterConfigurationStore.durationOptions);
      adHocDuration.value = isAdHoc ? props.duration : null;
      localDurationMode.value = props.durationMode;
    } else {
      adHocDuration.value = null;
    }
  }
);

const windowNights = computed(() => nightsInRange(localDateRange.value));

/**
 * Exact means "these dates exactly", but nothing is sold for longer than
 * {@link MAX_PRODUCT_DURATION} nights, so an exact search over a longer window
 * can only come back empty. A plain constant on purpose: the configured preset
 * list is per page, and capping by it would block Exact at 7 nights on a page
 * offering [3, 5, 7].
 */
const exactTooLong = computed(
  () => localDurationMode.value === 'exact' && windowNights.value > MAX_PRODUCT_DURATION
);

/**
 * The page's default length, never longer than the window it has to fit in.
 * With a hotel restriction the longest length that hotel sells within it.
 */
const fallbackDuration = computed(
  () =>
    (props.restriction?.durationsStatus === 'ready'
      ? props.restriction.fallbackDuration(windowNights.value)
      : null) ?? Math.max(1, Math.min(filterConfigurationStore.defaultDuration, windowNights.value))
);

const noDatesForOccupancy = computed(() => props.restriction?.arrivalStatus === 'empty');

const handleDateConfirm = () => {
  emit('update:dateRange', localDateRange.value);
  emit('update:duration', exactTooLong.value ? fallbackDuration.value : localDuration.value);
  emit('update:durationMode', exactTooLong.value ? 'preset' : localDurationMode.value);
  modalStore.closeModal(ModalType.DATE_PICKER);
};

const handleDurationUpdate = (duration: number) => {
  localDuration.value = duration;
};

const handleDurationModeUpdate = (mode: 'exact' | 'preset') => {
  localDurationMode.value = mode;
};
</script>

<template>
  <!-- Date Picker Modal -->
  <BaseModal
    v-model:open="isDatePickerModalOpen"
    :title="$t('ibe.modal.travel-period', 'Reisedauer und -zeitraum')"
    variant="centered"
    size="lg"
  >
    <BaseAlert
      v-if="exactTooLong"
      type="warning"
      class="mb-4"
      :message="
        $t(
          'ibe.duration.exact-too-long',
          'Exact dates are only available for stays of up to {max} nights. We will search for {n} nights instead.',
          { max: MAX_PRODUCT_DURATION, n: fallbackDuration }
        )
      "
    />

    <BaseAlert v-if="noDatesForOccupancy" type="info" class="mb-4">
      <I18nHtml
        key-name="ibe.datepicker.no-dates-for-occupancy"
        fallback="This accommodation currently has no travel dates for the selected travellers and rooms. Please change the travellers or the room allocation."
      />
    </BaseAlert>

    <DateAndDurationSelector
      v-model="localDateRange"
      :duration="localDuration"
      :duration-mode="localDurationMode"
      :ad-hoc-duration="adHocDuration"
      :min-date="minDate"
      :max-date="maxDate"
      :restriction="restriction"
      @update:duration="handleDurationUpdate"
      @update:duration-mode="handleDurationModeUpdate"
      @pending-change="selectionPending = $event"
    />

    <template #footer>
      <div class="w-full flex justify-center">
        <BaseButton variant="primary" :disabled="selectionPending" @click="handleDateConfirm">
          {{ $t('ibe.common.apply', 'Übernehmen') }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>
